import { z } from "zod";
import { IsoDateTime, OpaqueId } from "./common.js";
import { PositionType } from "./journey.js";
/**
 * Onglet Services (CDC §5.8, backlog Phase 2 P2-50), menu noir. Hors
 * régulation : « articuler les informations qu'on a pour les services »
 * (n° de service conducteur, **non nominatif**). Plan théorique issu des
 * documents « services conducteurs » (B6.1) + recoupement temps réel.
 *
 * Le n° de service n'est pas unique : il est scopé `(régime, groupe)` — la
 * fiche s'ouvre sur `/{groupLetters}/{serviceNumber}`.
 */
export const ServicePrestation = z.object({
    ligneRaw: z.string(), // code de ligne imprimé, ou « Dépl » / « RT » / « PMR » / « EMP »
    /** Ligne GTFS résolue depuis `ligneRaw` — `null` pour « Dépl »/« RT »/… ou ligne non mappée. */
    lineId: OpaqueId.nullable(),
    /** Couleur de la ligne (`#RRGGBB`, du GTFS `route_color`) — pour colorer la barre du Gantt. */
    lineColor: z.string().nullable(),
    sortieRaw: z.string().nullable(),
    kind: z.string().nullable(), // course | course-with-destination | deplacement | ramassage | reserve-tactique
    startSeconds: z.number().int().nullable(), // secondes depuis minuit du jour d'exploitation
    startPlaceRaw: z.string().nullable(), // abréviation du lexique
    startPlaceName: z.string().nullable(), // nom complet (`placeLexicon`) ou = raw si non mappé
    destinationRaw: z.string().nullable(), // destination annoncée (course-with-destination) ≠ lieu de fin
    destinationName: z.string().nullable(),
    endSeconds: z.number().int().nullable(),
    endPlaceRaw: z.string().nullable(),
    endPlaceName: z.string().nullable(),
});
export const ServiceReleve = z.object({
    ligneRaw: z.string(),
    lineId: OpaqueId.nullable(),
    lineColor: z.string().nullable(),
    sortieRaw: z.string(),
    atSeconds: z.number().int(),
    placeRaw: z.string(),
    placeName: z.string(),
    direction: z.enum(["in", "out"]), // in = prend la suite ; out = passe la main
    withServiceNumber: z.string(),
    withGroupLetters: z.string(),
});
/** Intervalle vide entre deux prestations consécutives (backlog Phase 2 P2-51). */
export const ServiceGap = z.object({
    afterIndex: z.number().int().nonnegative(), // le trou suit `prestations[afterIndex]`
    seconds: z.number().int().nonnegative(),
    /**
     * v1.53 — vocabulaire aligné sur le métier (règle utilisateur 2026-09-13) :
     * dans le service conducteur, le trou entre la fin d'une (ligne, sortie) et
     * la prestation suivante **est une pause**.
     *
     * - `battement` : trou sur la MÊME (ligne, sortie) — le véhicule attend, le
     *   conducteur reste dessus ;
     * - `changement-de-sortie` : il change de véhicule, mais trop vite pour que
     *   ce soit du repos (< `thresholds.driverMinimumBreakSeconds`) — temps de
     *   transfert ;
     * - **`pause`** : la pause réglementaire, entre deux sorties, au moins égale
     *   au minimum légal ;
     * - `coupure` : trou si long que le conducteur est hors service (service
     *   coupé), au-delà de `thresholds.driverSplitShiftSeconds`.
     *
     * Avant la v1.53, un seuil unique de 40 min tranchait entre
     * `changement-de-sortie` et `coupure` : il tombait **au milieu des vraies
     * pauses** (757 trous Astuce entre 23 et 40 min, 141 entre 40 min et 1 h) et
     * aucune pause n'était nommée comme telle.
     */
    kind: z.enum(["battement", "changement-de-sortie", "pause", "coupure"]),
    atPlaceRaw: z.string().nullable(),
    atPlaceName: z.string().nullable(),
});
export const ServiceLiveJourney = z.object({
    journeyId: OpaqueId,
    lineId: OpaqueId,
    lineCode: z.string(),
    runId: z.string().nullable(),
    vehicleDisplayNumber: z.string().nullable(),
    deviationSeconds: z.number().int(),
    positionType: PositionType,
});
export const ServicePlanResponse = z.object({
    date: z.string(), // YYYY-MM-DD
    period: z.string(),
    groupLetters: z.string(),
    serviceNumber: z.string(),
    workTimeSeconds: z.number().int().nullable(),
    startSeconds: z.number().int().nullable(),
    endSeconds: z.number().int().nullable(),
    prestations: z.array(ServicePrestation), // ordre chronologique
    gaps: z.array(ServiceGap), // trous qualifiés entre prestations (P2-51)
    releves: z.array(ServiceReleve),
    /** Courses actives portant ce n° de service (recoupement approché : n° seul). */
    live: z.object({ journeys: z.array(ServiceLiveJourney) }),
});
// --- Vue d'ensemble (bandeaux empilés, backlog Phase 2 P2-51) ----------
export const ServiceTimelinePrestation = z.object({
    ligneRaw: z.string(),
    lineColor: z.string().nullable(),
    sortieRaw: z.string().nullable(),
    kind: z.string().nullable(),
    startSeconds: z.number().int().nullable(),
    endSeconds: z.number().int().nullable(),
});
export const ServiceTimelineRow = z.object({
    groupLetters: z.string(),
    serviceNumber: z.string(),
    workTimeSeconds: z.number().int().nullable(),
    startSeconds: z.number().int().nullable(),
    endSeconds: z.number().int().nullable(),
    prestations: z.array(ServiceTimelinePrestation),
    gaps: z.array(ServiceGap),
    releveAtSeconds: z.array(z.number().int()), // instants de relève (pour un marqueur sur le bandeau)
});
export const ServicesTimelineResponse = z.object({
    date: z.string(),
    period: z.string(),
    truncated: z.boolean(),
    rows: z.array(ServiceTimelineRow), // triés par groupe puis n°
});
export const ActiveService = z.object({
    serviceNumber: z.string(),
    /** Groupe résolu via `journey.runId` (`{group}` pour ouvrir la fiche). `null` si non rapproché. */
    groupLetters: z.string().nullable(),
    lineIds: z.array(OpaqueId),
    lineCodes: z.array(z.string()),
    journeyCount: z.number().int().nonnegative(),
    vehicleDisplayNumbers: z.array(z.string()),
    runIds: z.array(z.string()),
    worstDeviationSeconds: z.number().int(), // écart le plus fort (valeur absolue) parmi ses courses
});
export const ActiveServicesResponse = z.object({
    at: IsoDateTime,
    period: z.string().nullable(),
    services: z.array(ActiveService), // triées par n°
});
/**
 * Outil « prochaine fin de service véhicule » (backlog Phase 2 P2-15) : les
 * véhicules dont la dernière prestation connue du jour se termine bientôt —
 * **« Calcul de véhicule rentrant »** (renommage utilisateur 2026-09-10) —
 * véhicules qui **rentrent au dépôt** bientôt : dernière course en ligne
 * terminée ou proche de l'être, puis Rentrée. Croisement course active ↔
 * marche de sortie (`sortie_schedule`, heure de Rentrée + dépôt imprimés).
 */
/** Où en est le véhicule vis-à-vis de sa Rentrée dépôt (v1.43). */
export const ReturnPhase = z.enum([
    "en-service", // encore une ou des courses en ligne à assurer
    "hlp-retour", // dernière course finie, en HLP vers le dépôt
    "rentre", // Rentrée théorique dépassée
]);
export const EndOfServiceItem = z.object({
    journeyId: OpaqueId,
    vehicleDisplayNumber: z.string().nullable(),
    lineId: OpaqueId,
    lineCode: z.string(),
    runId: z.string().nullable(),
    serviceNumber: z.string(), // n° de service conducteur si retrouvé (best-effort), sinon ""
    groupLetters: z.string().nullable(),
    endsAt: IsoDateTime, // fin de la dernière course EN LIGNE de la sortie
    inSeconds: z.number().int(), // avant `endsAt` ; peut être légèrement négatif
    // v1.41 — position courante du véhicule (carte + filtre `?bbox`). Toujours présente.
    lat: z.number(),
    lon: z.number(),
    // v1.43 — Rentrée dépôt
    depot: z.object({ id: z.string(), label: z.string() }).nullable(), // dépôt de config apparié ; null si `depotRaw` non reconnu
    depotRaw: z.string().nullable(), // libellé imprimé dans la marche de sortie
    phase: ReturnPhase,
    /**
     * v1.50 — provenance de `lat`/`lon` (et donc de `distanceToDepotMeters`).
     * `"scheduled"` = position **déduite de l'horaire**, aucun point GPS : la
     * distance au dépôt est alors un ordre de grandeur théorique, pas une mesure.
     */
    positionType: PositionType,
    /**
     * Instant de Rentrée théorique. `null` quand la marche n'imprime pas de
     * Rentrée exploitable comme retour de FIN de service — cas des services
     * coupés, dont la seule Rentrée imprimée est celle de mi-journée (19 % des
     * marches Astuce). Dans ce cas c'est `endsAt` qui fait foi.
     */
    rentreeAt: IsoDateTime.nullable(),
    rentreeInSeconds: z.number().int().nullable(), // avant la Rentrée ; négatif si dépassée ; null si `rentreeAt` l'est
    distanceToDepotMeters: z.number().nullable(), // à vol d'oiseau, position courante → dépôt apparié ; null si dépôt non apparié
});
export const EndOfServiceResponse = z.object({
    at: IsoDateTime,
    withinMinutes: z.number().int(),
    /** v1.41 — emprise appliquée si `?bbox=minLon,minLat,maxLon,maxLat` était fourni. */
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]).nullable(),
    // triés par fin de service croissante : la Rentrée quand elle est exploitable,
    // sinon la fin de la dernière course (v1.50 ; v1.43 triait sur la Rentrée seule)
    items: z.array(EndOfServiceItem),
});
//# sourceMappingURL=services.js.map